/**
 *  Tilby Custom Driver for Kube II F
 *
 *  Usage:
 *  CustomRTDriver.setup() and after call public methods:
 *
 *  - autoConfigurePrinter
 *  - printFiscalReceipt
 *  - printCourtesyReceipt
 *  - printNonFiscal
 *  - openCashDrawer
 *  - dailyClosing
 *  - dailyRead
 *
 *  - printFreeNonFiscal
 *
 */

import angular from 'angular';
import moment from 'moment-timezone';

import {
	filter,
	firstValueFrom,
	map,
	mergeMap,
	timeout,
	TimeoutError
} from 'rxjs';

import {
	ConfigurationManagerService,
	EntityManagerService
} from 'src/app/core';

import {
	SalePrintingUtilsService
} from 'src/app/features/cashregister';

import {
	ItalianFiscalPrinterDriver,
	ItPrinterConfigurationOptions,
	PrintFreeOptions
} from 'src/app/shared/model/it-fiscal-printer.model';

import {
	TilbyTcpSocket,
	TilbyTcpSocketUtils
} from 'src/app/shared/net/tilby-tcp-socket';

import {
	groupBy,
	inRange,
	keyBy,
	MathUtils
} from 'src/app/shared/utils';

import {
	padCenter,
	truncateString
} from 'src/app/shared/string-utils';

import {
	Departments,
	Printers,
	Sales,
	SalesDocuments,
	SalesItems,
	SalesItemsPriceChanges,
	SalesPriceChanges,
	Vat
} from 'tilby-models';

import {
	FiscalUtilsService
} from 'app/modules/core/service/fiscal-utils/fiscal-utils';

import {
	SimplifiedDocument
} from 'src/app/features/printers/drivers';

type printerCommands = Array<string[]>;
type printerResponses = Array<Uint8Array>;

type vatTableEntry = {
	id: number,
	exemption: number,
	name: string,
	value: string
}

type customPrinterSocket = undefined | {
	printer: Printers,
	messageIndex: number,
	printerGen?: number,
	tcpSocket: TilbyTcpSocket,
};

const defaultVatsTable: Record<number | string, vatTableEntry> = {
	22: { id: 1, value: '2200', exemption: 1, name: "IVA 22%" },
	10: { id: 2, value: '1000', exemption: 1, name: "IVA 10%" },
	4: { id: 3, value: '0400', exemption: 1, name: "IVA 4%" },
	'N4': { id: 6, value: '0000', exemption: 4, name: "IVA Esente ES N4" },
	'N1': { id: 7, value: '0000', exemption: 1, name: "IVA Esente EE N1" },
	'N2': { id: 8, value: '0000', exemption: 2, name: "IVA Esente NS N2" },
	'N3': { id: 9, value: '0000', exemption: 3, name: "IVA Esente NI N3" },
	'N5': { id: 10, value: '0000', exemption: 5, name: "IVA Esente RM N5" },
	'N6': { id: 11, value: '0000', exemption: 6, name: "IVA Esente AL N6" }
};

const errorsTable: Record<number, string> ={
	54: 'FISCAL_CLOSING_NEEDED'
};

const barcodeTypeCodes = {
	'EAN13': '01',
	'EAN8': '02',
	'CODE39': '03',
	'EAN128': '04',
	'QRCODE': '06',
	'UPC': '09'
};

const byteArrayToString = (byteArray?: Uint8Array) => byteArray ? Array.from(byteArray).map((val) => String.fromCharCode(val)).join('') : '';

const getLineLength = (line: string, paddingSize: number = 2): string => line.length.toString().padStart(paddingSize, '0');

const cleanUpSpecialChars = (str?: string) => (str || '').replace("€", "E").replace(/[^\x00-\x7F]/g, "*");

export class CustomRTDriver implements ItalianFiscalPrinterDriver {
	private printer?: Printers;
	private options: any = null;

	static $inject = [
		"fiscalUtils",
		"checkManager",
		"entityManager",
		"salePrintingUtils"
	];

	constructor(
		private fiscalUtils: FiscalUtilsService,
		private configurationManagerService: ConfigurationManagerService,
		private entityManagerService: EntityManagerService,
		private salePrintingUtils: SalePrintingUtilsService
	) {
	}

	private logEvent(...message: any[]) {
		console.debug('[ CustomRTDriver ]', ...message);
	};

	private getPacket(command: string, index: number) {
		const packetData = [];

		//Index
		packetData.push(Math.floor(index / 10) + 0x30, (index % 10) + 0x30);

		//Ident
		packetData.push(0x30);

		//DATA
		for (let i = 0; i < command.length; i++) {
			packetData.push(command.charCodeAt(i));
		}

		//Checksum
		const dataSum = packetData.reduce((acc, num) => acc + num, 0) % 100;
		const checkSum = [Math.floor(dataSum / 10) + 0x30, dataSum % 10 + 0x30];

		return new Uint8Array([0x02, ...packetData, ...checkSum, 0x03]);
	}

	private async closeSocket(socket: customPrinterSocket): Promise<void> {
		if (socket) {
			await socket.tcpSocket.disconnect();
		}
	}

	private getMaxDepartment (printerInfo: customPrinterSocket): number {
		return 30;
	};

	private async sendCommands(printerSocket: customPrinterSocket, commands: printerCommands): Promise<printerResponses> {
		if (!printerSocket) {
			throw 'INVALID_SOCKET';
		}

		let currentCommand: string;

		const responseData = printerSocket.tcpSocket.onData.pipe(
			//Slice piggy-backed responses in two packets
			map((packet: Uint8Array) => {
				//Happens when ack and data are in the same packet. In this case split the ack and the packet data in two separate packets
				if (packet[0] === 0x06 && packet.length > 1) {
					return [packet.slice(0, 1), packet.slice(1)];
				}

				return [packet];
			}),
			mergeMap((packets: Uint8Array[]) => packets),
			//Ignore ack packets
			filter((packet: Uint8Array) => (packet[0] != 0x06)),
			//Figure out responses to commands
			mergeMap((packet: Uint8Array) => {
				const response = [];

				switch (packet[0]) {
					case 0x02: //Actual command response
						const responseData = packet.slice(4, packet.length - 3);
						const command = byteArrayToString(responseData.slice(0, 4));

						//Throw printer errors
						if (byteArrayToString(responseData.slice(0, 3)) === 'ERR') {
							const errorCode = parseInt(byteArrayToString(responseData.slice(3, 5))) || 0;

							throw (errorsTable[errorCode] || `CUSTOM.ERROR_${errorCode}`);
						}

						if (command !== currentCommand) {
							break;
						}

						//Throw specific "Already void" error message
						if (command === '7101' && byteArrayToString(responseData.slice(4)) !== '1') {
							throw 'RT_ALREADY_VOID';
						}

						//Otherwise we have a response
						response.push(responseData.slice(4));
						break;
					case 0x15: //NACK: something went wrong, abort
					default: //Everything else (unexpected)
						throw 'COMMUNICATION_ERROR';
				}

				return response;
			}),
			timeout({ each: 10000 })
		);

		const responses: printerResponses = [];

		try {
			for (const command of commands) {
				const printerCommand = command.join('');
				currentCommand = printerCommand.slice(0, 4);

				//Prepare listening to response and send the command
				const commandResponse = await new Promise<Uint8Array>((resolve, reject) => {
					firstValueFrom(responseData).then(resolve, reject);

					printerSocket.tcpSocket.send(this.getPacket(printerCommand, printerSocket.messageIndex++)).catch(reject);
				});

				responses.push(commandResponse);
			}
		} catch (error) {
			if (error instanceof TimeoutError) {
				throw 'REQ_TIMEOUT';
			}

			throw error
		}

		return responses;
	}

	private async isAvailable(printer: Printers): Promise<customPrinterSocket> {
		let tcpSocket = TilbyTcpSocketUtils.getSocket();

		if (!tcpSocket) {
			throw 'UNSUPPORTED_ENVIRONMENT';
		}

		if(!printer) {
			throw 'MISSING_PRINTER';
		}

		try {
			await tcpSocket.connect(printer.ip_address!, printer.port || 9100, { timeout: 10000 });
		} catch (error) {
			throw 'CONNECTION_ERROR';
		}

		const printerSocket: customPrinterSocket = {
			printer: printer,
			tcpSocket: tcpSocket,
			messageIndex: 0
		}

		try {
			const printerGenInfo = await this.sendCommands(printerSocket, [['1214']]);
			printerSocket.printerGen = parseInt(byteArrayToString(printerGenInfo.shift())) || 0;
		} catch (error) {
			this.closeSocket(printerSocket);
			throw error;
		}

		return printerSocket;
	}

	private async connectAndInitializePrinter(printer: Printers): Promise<customPrinterSocket> {
		let printerSocket: customPrinterSocket;

		try {
			printerSocket = await this.isAvailable(printer);
			await this.clearPendingDocuments(printerSocket);
		} catch (error) {
			this.closeSocket(printerSocket);
			throw error;
		}

		return printerSocket;
	}

	private async clearPendingDocuments(printerSocket: customPrinterSocket): Promise<number> {
		const pendingDocumentResp = await this.sendCommands(printerSocket, [['1012']])
			.then(responses => responses.pop()?.[0]);

		if(pendingDocumentResp == null) {
			return 0;
		}

		const pendingDocumentStep = pendingDocumentResp - 0x30;

		const unlockCommands: printerCommands = [];

		if (pendingDocumentStep === 8) {
			//Pending non-fiscal document
			unlockCommands.push(['4004']);
		} else if (pendingDocumentStep > 0) {
			//Pending fiscal document
			if (pendingDocumentStep < 5) {
				unlockCommands.push(
					['3001', '8', '17', 'Annullo scontrino', '000000000'],
					['3011']
				);
			}

			unlockCommands.push(['3013']);
		}

		if (unlockCommands.length) {
			await this.sendCommands(printerSocket, unlockCommands);
		}

		return pendingDocumentStep;
	}

	/**
	 * linesToCommands
	 */
	private linesToCommands(lines: string | string[], options?: PrintFreeOptions): printerCommands {
		if (!Array.isArray(lines)) {
			if (typeof lines === 'string') {
				lines = lines.split("\n");
			}
		}

		// Send CL
		const commands: printerCommands = [['4001', '1', '0', '000000000']];

		for (const line of lines) {
			const l = cleanUpSpecialChars(line).slice(0, 42);
			const lineLength = getLineLength(l);

			commands.push(['4003', '3', lineLength, l]);
		}

		if (options?.barcode) {
			const barcodeType = barcodeTypeCodes[options.barcode.type] || '03';
			commands.push(['3023', barcodeType, '2', '4', getLineLength(options.barcode.value, 3), options.barcode.value])
		}

		commands.push(['4004']);

		return commands;
	}

	private getPrinterVatIdFromDepartment = (department?: Departments): string => {
		if (!department?.vat) {
			throw 'OUT_OF_RANGE_VAT';
		}

		let vatId;

		if (department.vat.value === 0) { //In case of exemptions, use the defaultVatsTable
			const vatToUse = defaultVatsTable[department.vat.code || 'N4'];

			if (!vatToUse) {
				throw 'OUT_OF_RANGE_VAT'
			}

			vatId = vatToUse.id;
		} else {
			if (!inRange(department.vat.id, 1, 5)) {
				throw 'OUT_OF_RANGE_VAT';
			}

			vatId = department.vat.id
		}

		return vatId.toString().padStart(2, '0');
	}

	private getRowTypeFromSaleItem(saleItem: SalesItems, isRefunding: boolean): string {
		switch (saleItem.type) {
			case 'sale':
				return '1';
			case 'refund':
				return isRefunding ? '1' : '4';
			case 'gift':
				return 'G';
			case 'deposit_cancellation':
				return 'F';
			default:
				return '1';
		}
	}

	/**
	 *  saleToReceiptCommands
	 */
	private saleToReceiptCommands(printerInfo: customPrinterSocket, sale: Sales, options: any, departments: Departments[]): printerCommands {
		const printerMaxDepartments = this.getMaxDepartment(printerInfo);

		if(sale.sale_items?.some((saleItem) => (saleItem.department_id || 0) > printerMaxDepartments)) {
			throw "NOT_ENOUGH_DEPARTMENTS";
		}

		const printNotes = this.options.print_notes !== false;
		const printBarcodeId = this.options.print_barcode_id !== false;
		const ticketChange = this.options.ticket_change !== false;
		const printCustomerDetail = this.options.print_customer_detail !== false;
		const printSeller = this.options.print_seller !== false;

		const commands: printerCommands = [['3016', '1']]; //TODO: clear printer before sending sale commands
		const departmentsByCode = keyBy(departments, d => d.printer_code);

		const addTextLine = (text: string) => {
			const textToAdd = text.slice(0, 32);
			commands.push(['3002', '1', getLineLength(textToAdd), textToAdd]);
		};

		const isRefund = this.fiscalUtils.isRefundVoidSale(sale);

		if (isRefund) {
			const docDataItem = sale.sale_items?.find((saleItem) => (saleItem.reference_sequential_number && saleItem.reference_date));

			if (!docDataItem) {
				throw 'MISSING_REFERENCE_DOCUMENT';
			}

			const itemsFromSameDocument = !!sale.sale_items?.every(saleItem => saleItem.reference_sequential_number === docDataItem.reference_sequential_number && saleItem.reference_date === docDataItem.reference_date);

			if (!itemsFromSameDocument) {
				throw 'ITEMS_FROM_DIFFERENT_DOCUMENTS'
			}

			//Check if we are doing a void or a refund
			let hasVoid = false;
			let hasRefund = false;

			for (const saleItem of (sale.sale_items || [])) {
				if (saleItem.refund_cause_id === 6) {
					hasVoid = true;
				} else {
					hasRefund = true;
				}
			}

			if (hasRefund && hasVoid) {
				throw 'MIXED_REFUND_CAUSES';
			}

			const docSeqNum = this.fiscalUtils.parseRTDocumentSequentialNumber(docDataItem.reference_sequential_number);
			const documentDate = moment(docDataItem.reference_date).format('DDMMYY');
			const dcNum = docSeqNum.daily_closing_num.toString().padStart(4, '0');
			const seqNum = docSeqNum.document_sequential_number.toString().padStart(4, '0');

			if (hasVoid) {
				commands.push(['7101', 'A', dcNum, seqNum, documentDate, '0']);
				commands.push(['7101', 'A', dcNum, seqNum, documentDate, '1']);

				return commands;
			} else {
				commands.push(['7101', 'R', dcNum, seqNum, documentDate, '0']);
				commands.push(['7101', 'R', dcNum, seqNum, documentDate, '1']);
			}
		} else {
			// Sale Name
			const printName = this.options.print_name !== false;

			if (printName) {
				for(const row of this.fiscalUtils.getFiscalReceiptHeaderLines(sale)) {
					addTextLine(`# ${row}`);
				}
			}
		}

		const appendPriceChanges = (priceChanges: SalesPriceChanges[] | SalesItemsPriceChanges[], departmentId?: number) => {
			for (const priceChange of priceChanges) {
				const pcAmount = priceChange.amount || 0;

				if(!pcAmount) {
					continue;
				}

				if (priceChange.type === 'gift') {
					commands.push(['3001', 'G', '00', '000000000']);
				} else {
					const adjustmentType = pcAmount < 0 ? '3' : '2';
					const pcDesc = truncateString(priceChange.description, 44);

					if (departmentId != null) {
						const departmentCode = departmentId.toString().padStart(3, '0');
						const vatId = this.getPrinterVatIdFromDepartment(departmentsByCode[departmentId]);

						commands.push([
							'3301',
							adjustmentType,
							'000000000',
							departmentCode,
							getLineLength(pcDesc),
							pcDesc,
							Math.round(Math.abs(pcAmount) * 100).toString().padStart(9, '0'),
							vatId
						]);
					} else {
						commands.push([
							'3001',
							adjustmentType,
							getLineLength(pcDesc),
							pcDesc,
							Math.round(Math.abs(pcAmount) * 100).toString().padStart(9, '0')
						]);
					}
				}
			}
		};

		const appendItems = (saleItems: SalesItems[]) => {
			for (const saleItem of saleItems) {
				const siName = truncateString(saleItem.name || saleItem.department_name || '', 44);
				const department = departmentsByCode[saleItem.department_id];
				const departmentCode = saleItem.department_id.toString().padStart(3, '0');
				const vatId = this.getPrinterVatIdFromDepartment(department);

				commands.push([
					'3301',
					this.getRowTypeFromSaleItem(saleItem, isRefund),
					Math.abs(Math.round(saleItem.quantity * 1000)).toString().padStart(9, '0'),
					departmentCode,
					getLineLength(siName),
					siName,
					Math.round(saleItem.price * 100).toString().padStart(9, '0'),
					vatId
				]);

				if (!isRefund) {
					// Item barcode (if print_notes)
					if (printNotes && saleItem.barcode) {
						if (saleItem.barcode.toLowerCase().indexOf('p') < 0 && saleItem.barcode.toLowerCase().indexOf('q') < 0) {
							addTextLine(`# ${saleItem.barcode.trim()}`);
						}
					}

					// Notes
					if (printNotes && saleItem.notes) {
						for (const noteLine of saleItem.notes.split("\n")) {
							const nl = noteLine.trim();

							if (nl) {
								addTextLine(`# ${cleanUpSpecialChars(nl)}`);
							}
						}
					}

					// Refund cause
					if (saleItem.quantity < 0 && saleItem.refund_cause_description) {
						addTextLine(`# ${cleanUpSpecialChars(saleItem.refund_cause_description)}`);
					}

					// Discount / Surcharges
					if (saleItem.quantity > 0 && saleItem.price_changes?.length) {
						appendPriceChanges(saleItem.price_changes, saleItem.department_id);
					}

					if (saleItem.type === 'gift') {
						addTextLine("# Omaggio");
					}
				}
			}
		};

		const appendSubtotal = () => commands.push(['3003']);

		this.fiscalUtils.applyFiscalBody(sale, appendItems, appendPriceChanges, appendSubtotal);

		if(!isRefund) {
			// Payments
			if (sale.final_amount! >= 0) {
				//Add lottery code if available
				if (sale.lottery_code) {
					commands.push(['3019', sale.lottery_code.length.toString().padStart(2, '0'), sale.lottery_code]);
				}

				const payments = this.fiscalUtils.extractPayments(sale);

				const itemsBySalesType = groupBy(sale.sale_items || [], (saleItem) => departmentsByCode[saleItem.department_id]?.sales_type);
				//Use unclaimed (goods) if the sale only contains goods, otherwise use unclaimed (services)
				const unclaimedType = (itemsBySalesType.goods?.length && !itemsBySalesType.services?.length) ? '07' : '50';

				for (const payment of payments) {
					const paymentName = cleanUpSpecialChars(payment.method_name).slice(0, 22);
					let paymentCode;

					//Assign payment type
					switch (payment.method_type_id) {
						case 1: case 19: case 21: case 32: case 38: case 39: case 40:
							paymentCode = '01'; //Cash
							break;
						case 3:
							paymentCode = '02'; //Cheque
							break;
						case 4: case 5: case 8: case 11: case 13: case 14: case 15: case 17: case 18: case 27: case 30: case 31: case 35: case 37: case 44: case 45:
							paymentCode = '03'; //Credit card
							break;
						case 6: case 34:
							paymentCode = '05'; //Ticket
							break;
						case 2:
							paymentCode = unclaimedType;
							break;
						case 22: case 23: case 24: case 28: case 29: case 36: case 41: case 43:
							paymentCode = '51';
							break;
						case 25:
							paymentCode = '52';
							break;
						case 10: case 26: case 33:
							paymentCode = '53'; //Discount on payment
							break;
						case 16: case 42: case 46:
							paymentCode = payment.unclaimed ? unclaimedType : '08';
							break;
						default:
							paymentCode = '01';
							break;
					}

					commands.push([
						'3007',
						paymentCode,
						'01',
						getLineLength(paymentName),
						paymentName,
						Math.round(payment.amount * 100).toString().padStart(9, '0')
					]);
				}
			}

			// Barcode
			if (printBarcodeId && Number.isInteger(sale.id)) {
				const saleIdBarcode = sale.id!.toString();
				commands.push(['3021', '3', '2', '4', '0', getLineLength(saleIdBarcode), cleanUpSpecialChars(saleIdBarcode)]);
			}

			const customerTaxCode: string | undefined = sale.sale_customer?.tax_code || sale.customer_tax_code;

			if (customerTaxCode && this.fiscalUtils.checkTaxCode(customerTaxCode)) {
				commands.push(['3010', 'B', getLineLength(customerTaxCode), customerTaxCode]);
			}

			//Close fiscal document
			commands.push(['3011']);

			const addTextLine = (text: string) => {
				const textToAdd = cleanUpSpecialChars(text.slice(0, 32)) || ' '.repeat(32);

				commands.push(['3012', '1', getLineLength(textToAdd), textToAdd]);
			};

			// Ticket change
			if (ticketChange && sale.change && sale.change > 0 && sale.change_type === 'ticket') {
				const ticketChangeAmount = MathUtils.round(sale.change);

				addTextLine(`######### RESTO TICKET #########`);
				addTextLine(`                                `);
				addTextLine(` QUESTO COUPON VALE: ${ticketChangeAmount.toFixed(2)} EURO`);
				addTextLine(`                                `);
				addTextLine(` Presenta alla cassa prima del  `);
				addTextLine(` pagamento questo coupon per    `);
				addTextLine(` avere riconosciuto il credito. `);
				addTextLine(` Valido 30 giorni dalla data    `);
				addTextLine(` di emissione.                  `);
				addTextLine(` Non convertibile in denaro.    `);
				addTextLine(`                                `);

				const ticketBarcode = (1212000000000 + Math.round(ticketChangeAmount * 100)).toString();
				commands.push(['3023', '01', '2', '4', getLineLength(ticketBarcode, 3), cleanUpSpecialChars(ticketBarcode)]);

				addTextLine(`                                `);
				addTextLine(`################################`);
				addTextLine(`                                `);
			}

			// Add Tail (tail for this sale + general tail)
			let tail = options.tail || '';

			if (this.options.tail) {
				tail += "\n" + this.options.tail;
			}

			if (tail) {
				const tails = tail.split('\n');

				for (const tRow of tails) {
					addTextLine(tRow);
				}
			}

			// Customer
			if (printCustomerDetail && sale.sale_customer) {
				for (const line of this.fiscalUtils.getCustomerInfo(sale.sale_customer)) {
					addTextLine(line);
				}
			}

			// Print Seller
			if (printSeller && sale.seller_name) {
				addTextLine(`${this.options.seller_prefix || "Ti ha servito"} ${sale.seller_name.trim().split(" ")[0]}`);
			}

			// Payment tail */
			if (options.tail && options.tail.indexOf('FIRMA') > -1) {
				// Add Payment Tail
				for (const tRow of options.tail.split('\n')) {
					addTextLine(tRow);
				}
			}

			commands.push(['3013']);
		} else {
			commands.push(['3011'], ['3013']);
		}

		return commands;
	}

	/**
	 * @description saleToNonFiscalCommands
	 */
	private saleToNonFiscalCommands(printer: Printers, sale: Sales, options: any) {
		const commands: printerCommands = [];

		const savePaper = !!this.configurationManagerService.getPreference('cashregister.save_paper_on_prebill');
		const printerColumns = printer.columns || 42;

		// Prepare tail text
		let tail = '';

		if (options.tail && typeof options.tail === 'string') {
			tail += options.tail;
		}

		if (this.options.tail && typeof this.options.tail === 'string') {
			tail += '\n' + this.options.tail;
		}

		// Use SalePrintingUtilsService to generate document lines
		const documentLines = this.salePrintingUtils.saleToNonFiscalDocument(sale, {
			printerColumns: printerColumns,
			tail: tail
		});

		commands.push(['4001', '1', '0', '000000000']);

		const addLine = (line: string) => {
			const l = cleanUpSpecialChars(line).slice(0, 42);
			const lineLength = getLineLength(l);

			commands.push(['4003', '3', lineLength, l]);
		};

		const printName = !(savePaper || this.options.print_name === false);

		if(printName) {
			for(const row of this.fiscalUtils.getFiscalReceiptHeaderLines(sale)) {
				addLine(row);
			}
		}

		// Process document lines
		for (const line of documentLines) {
			switch (line.type) {
				case 'text':
					addLine(line.align === 'center' ? padCenter(line.content, printerColumns) : line.content);
					break;
				case 'qrcode':
					commands.push(['3023', barcodeTypeCodes.QRCODE, '0', '4', getLineLength(line.content, 3), line.content]);
					break;
			}
		}

		commands.push(['4004']);

		// Open cash drawer if allowed
		const openCashdrawer = options.can_open_cash_drawer !== false;

		if (openCashdrawer) {
			commands.push(['7008', '1']);
		}

		return commands;
	}

	/**
	 * saleToCourtesyReceiptCommands
	 */
	private saleToCourtesyReceiptCommands(sale: Sales) {
		const lines = [];
		const courtesyReceiptOptions: PrintFreeOptions = {};

		lines.push("SCONTRINO DI CORTESIA");
		lines.push("");
		lines.push(sale.name);
		lines.push("");

		// Items
		if (Array.isArray(sale.sale_items)) {
			for (const saleItem of sale.sale_items) {
				lines.push(saleItem.quantity + "x " + cleanUpSpecialChars(saleItem.name || saleItem.department_name));
			}
		}

		if (sale.id && Number.isInteger(sale.id)) {
			courtesyReceiptOptions.barcode = {
				type: 'CODE39',
				value: sale.id.toString()
			}
		}

		return this.linesToCommands(lines, courtesyReceiptOptions);
	}

	/**
	 *  configurationToCommands
	 *  @param like autoConfigurePrinter parameters
	 */
	private async configurationToCommands(printerSocket: customPrinterSocket, options: ItPrinterConfigurationOptions) {
		const resources = await this.fiscalUtils.getPrinterConfigurationResources();
		const commands: printerCommands = [];

		if(options.configure_header) {
			// Configure headers
			for (let i = 1; i <= 9; i++) {
				const headerRow = cleanUpSpecialChars(options[`header${i}` as (`header${number}` & keyof ItPrinterConfigurationOptions)] || '').slice(0, 44);
				commands.push(['6302', i.toString(), '1', getLineLength(headerRow), headerRow]);
			}
		}

		// Vat setup (only non-exemptions, limited to vats from id 1 to 5 since the printer can only save 5 VATs)
		const vatsToConfigure = resources.vats.filter((vat) => (vat.value != 0 && vat.code == null));
		const vatsById: Record<number, Vat> = {};

		for (const vat of vatsToConfigure) {
			if (inRange(vat.id, 1, 5) && !vat.code) {
				//keep VAT in dictionary to use it on the departments configuration
				vatsById[vat.id] = vat;

				const vatName = `IVA ${vat.value.toString()}%`;
				commands.push(['7113', vat.id.toString().padStart(2, '0'), Math.round(vat.value * 100).toString().padStart(4, '0'), '1', getLineLength(vatName), vatName]);
			}
		}

		const maxDepartments = this.getMaxDepartment(printerSocket);

		// Departments setup
		for (const department of resources.departments) {
			if(department.printer_code == null || department.printer_code > maxDepartments) {
				this.logEvent(`Department ${department.id} has an out of range code`);
				continue;
			}

			if(!department.vat) {
				this.logEvent(`Department ${department.id} has no VAT`);
				continue;
			}

			const depName = cleanUpSpecialChars(department.name).slice(0, 16);
			let vatValue: number | undefined;

			switch (printerSocket!.printerGen) {
				case 2:
				case 4:
					vatValue = department.vat.value;
					break;
				case 3:
					vatValue = department.vat.value === 0
						? defaultVatsTable[department.vat.code || 'N2']?.id
						: vatsById[department.vat.id]?.id
					break;
			}

			if (vatValue == null) {
				throw 'OUT_OF_RANGE_VAT';
			}

			commands.push(['7009', department.printer_code.toString().padStart(2, '0'), getLineLength(depName), depName, '00010', vatValue.toString().padStart(2, '0')]);
		}

		// Display message
		if (options.display_message) {
			const displayMessage = cleanUpSpecialChars(options.display_message).slice(0, 48);

			commands.push(['7010', '0', getLineLength(displayMessage), displayMessage]);
		}

		return commands;
	}

	private async lastReceiptInfo(printerSocket: customPrinterSocket) {
		const result: Partial<SalesDocuments> = {};

		//Obtain metadata about last emitted receipt
		const receiptsData = await this.sendCommands(printerSocket, [['1001'], ['1008'], ['1003'], ['1104']]);
		const date = moment(byteArrayToString(receiptsData.shift()), 'DDMMYYHHmm');
		const serialResponse = receiptsData.shift();
		const receiptNumber = parseInt(byteArrayToString(receiptsData.shift()?.slice(56, 60)));
		const dailyClosingNumber = parseInt(byteArrayToString(receiptsData.shift()?.slice(0, 4))) + 1;

		//Find last emitted receipt in the DGFE
		let receiptLines: string[];

		try {
			const receiptNumberString = receiptNumber.toString().padStart(4, '0');
			const receiptDgfeLocationResp = await this.sendCommands(printerSocket, [['8006', dailyClosingNumber.toString().padStart(4, '0'), receiptNumberString, receiptNumberString]]);
			const receiptDgfeLocation = receiptDgfeLocationResp.shift();
			receiptLines = await this.readDgfeLines(printerSocket, parseInt(byteArrayToString(receiptDgfeLocation?.slice(0, 9))), parseInt(byteArrayToString(receiptDgfeLocation?.slice(9, 18))) - 1);
		} catch (err) {
			receiptLines = [];
		}

		Object.assign(result, {
			printer_serial: `${byteArrayToString(serialResponse?.slice(0, 2))} ${byteArrayToString(serialResponse?.slice(2))}`,
			sequential_number: (dailyClosingNumber * 10000) + receiptNumber,
			document_content: receiptLines.join('\n') || null,
			date: date.toISOString()
		});

		return result;
	}

	private async readDgfeLines(printerSocket: customPrinterSocket, lineFrom: number, lineTo: number): Promise<string[]> {
		const result: string[] = [];

		if (Number.isInteger(lineFrom) && Number.isInteger(lineTo) && lineTo != 0 && lineFrom <= lineTo) {
			for (let i = lineFrom; i <= lineTo; i++) {
				const response = await this.sendCommands(printerSocket, [['8004', i.toString().padStart(9, '0')]]);
				const row = byteArrayToString(response[0]);

				result.push(row);
			}
		}

		return result;
	}

	/**
	 *  Public methods
	 */
	/**
	 * @desc setup RCH Print!F printer
	 *
	 * @param printer, the tilby fiscal printer (must be an RCH Print!F obviously)
	 * @param options.print_notes: boolean (default true), if true prints the non-fiscal lines above each sale_item (for ex. variations, or combinations)
	 * @param options.print_name: boolean (default true), if true prints the sale.name on top
	 * @param options.print_barcode_id: boolean (default true), if true, prints a barcode on bottom that contains the sale.id
	 * @param options.ticket_change: boolean (default true), if true enables ticket change
	 * @param options.print_customer_detail: boolean (default true), if true print TAX_CODE, FIRST_NAME, LAST_NAME, COMPANY_NAME, VAT_CODE, FIDELITY
	 * @param options.print_seller: boolean (default true), if true print seller
	 * @param options.seller_prefix: string, customize seller phrase
	 * @param options.tail: string, general tail for the receipts
	 *
	 * @param printer.id: integer, the printer id
	 * @param printer.name: string, the printer name
	 * @param printer.driver: string, must be 'rch in this case'
	 * @param printer.ip_address: string, printer IP (if using webservice mode)
	 */
	public setup(printer: Printers, options: any) {
		if (!printer) {
			throw "Printer is undefined";
		} else if (printer.driver !== 'custom') {
			throw "Wrong driver";
		} else if (!printer.ip_address) {
			throw "Missing ip_address";
		}

		this.printer = printer;
		this.options = options;
	}

	public async getPrinterStatus() {
		const result = {};
		let printerSocket: customPrinterSocket;

		try {
			if(!this.printer) {
				throw 'MISSING_PRINTER';
			}

			printerSocket = await this.isAvailable(this.printer);

			//TODO: add periodic check and inactive period
			const commands: printerCommands = [
				['1008'], //Printer Serial
				['1209'], //Printer Status [CoverOpen, PaperEnd, PaperAlmostEnd, DgfeEnd, DgfeAlmostEnd, PaperCutError]
				['1511'], //RT Status
				['15140'], //RT Daily Closing Status
				['1013'] //Printer release
			];

			const responses = await this.sendCommands(printerSocket, commands);
			const serialResponse = responses.shift();
			const mainPrinterStatus = byteArrayToString(responses.shift());
			const rtLogicStatus = responses.shift();
			const rtDCStatus = responses.shift();
			const printerFw = responses.shift();

			const fpStatus = Array(5).fill('0');

			//Cover/Paper status
			fpStatus[0] = (mainPrinterStatus[0] === '1' || mainPrinterStatus[1] === '1' || mainPrinterStatus[2] === '1') ? '3' : '0';

			//Dgfe Status
			if (mainPrinterStatus[4] === '1') {
				fpStatus[1] = '1';
			}

			if (mainPrinterStatus[4] === '1') {
				fpStatus[1] = '5';
			}

			//RT Status
			const rtStatus = byteArrayToString(rtLogicStatus?.slice(22, 25));

			if (rtStatus[0] === '0') {
				Object.assign(result, { rtMainStatus: '01', rtSubStatus: '05' });
			} else if (rtStatus[1] === '0') {
				Object.assign(result, { rtMainStatus: '01', rtSubStatus: '06' });
			} else if (rtStatus[2] === '0') {
				Object.assign(result, { rtMainStatus: '01', rtSubStatus: '07' });
			} else {
				Object.assign(result, { rtMainStatus: '02', rtSubStatus: '08' });
			}

			Object.assign(result, {
				fpStatus: fpStatus.join(''),
				cpuRel: byteArrayToString(printerFw).trim(),
				rtType: byteArrayToString(rtLogicStatus?.slice(0, 1)) === '1' ? 'I' : 'M',
				rtFileToSend: byteArrayToString(rtDCStatus?.slice(5, 9)),
				rtFileRejected: byteArrayToString(rtDCStatus?.slice(1, 5)),
				printer_serial: `${byteArrayToString(serialResponse?.slice(0, 2))} ${byteArrayToString(serialResponse?.slice(2))}`
			});

			return result;
		} finally {
			this.closeSocket(printerSocket);
		}
	}

	/**
	 *  @desc automagically configure RCH Print!F
	 *
	 *  ( /printers )
	 *  @param printer, the printer you want to configure
	 *  @param printer.invoice_prefix (from printer)
	 *  @param printer.printer_number (from printer)
	 *
	 *  ( /shop_preferences )
	 *
	 *  @param options.header1 (max 48 chars - receipt + invoice)
	 *  @param options.header2 (max 48 chars - receipt + invoice))
	 *  @param options.header3 (max 48 chars - receipt + invoice))
	 *  @param options.header4 (max 48 chars - receipt + invoice))
	 *  @param options.header5 (max 48 chars - receipt + invoice))
	 *  @param options.header6 (max 48 chars - receipt + invoice))
	 *
	 *  @param options.header7 (max 48 chars - invoice only)
	 *  @param options.header8 (max 48 chars - invoice only)
	 *  @param options.header9 (max 48 chars - invoice only)
	 *  @param options.header10 (max 48 chars - invoice only)
	 *  @param options.header11 (max 48 chars - invoice only)
	 *  @param options.header12 (max 48 chars - invoice only)
	 *  @param options.header13 (max 48 chars - invoice only)
	 *
	 *  @param options.invoice_footer1 (max 48 chars)
	 *  @param options.invoice_footer2 (max 48 chars)
	 *  @param options.invoice_footer3 (max 48 chars)
	 *  @param options.invoice_footer4 (max 48 chars)
	 *  @param options.invoice_footer5 (max 48 chars)
	 *  @param options.invoice_footer6 (max 48 chars)
	 *
	 *  @param options.display_message
	 *
	 */
	public async autoConfigurePrinter(printer: Printers, options: ItPrinterConfigurationOptions): Promise<void> {
		// Configuring via TCP SOCKET
		let printerSocket: customPrinterSocket;

		try {
			printerSocket = await this.connectAndInitializePrinter(printer);

			this.logEvent(`Autoconfiguring ${printer.name} ${printer.ip_address} via tcp...`);
			const commands = await this.configurationToCommands(printerSocket, options);

			if (!Array.isArray(commands)) {
				throw commands;
			}

			await this.sendCommands(printerSocket, commands);
		} catch (error) {
			if (error === 'CUSTOM.ERROR_5') {
				throw 'CUSTOM.CONFIGURATION_FAILED';
			} else {
				throw error;
			}
		} finally {
			this.closeSocket(printerSocket);
		}
	}

	/**
	 * @desc print a commercial document (also known as 'documento commerciale', for italians)
	 *
	 * @param sale - the tilby sale you want to print
	 * @param options.can_open_cash_drawer - true if user has permission, false otherwise
	 * @param options.tail: string, print this string at the end of receipt
	 *
	 * @return successFunction with printed sale or errorFunction with errors
	 */
	public async printFiscalReceipt(sale: Sales, options: any, successFunction: Function, errorFunction: Function) {
		let printerSocket: customPrinterSocket;

		try {
			if(!this.printer) {
				throw 'MISSING_PRINTER';
			}

			printerSocket = await this.connectAndInitializePrinter(this.printer);
			const isRefundVoid = this.fiscalUtils.isRefundVoidSale(sale);

			const departments = await this.entityManagerService.departments.fetchCollectionOffline();
			const commands = this.saleToReceiptCommands(printerSocket, sale, options, departments);

			if (!Array.isArray(commands)) {
				throw commands;
			}

			// Open cash drawer
			const openCashdrawer = (options.can_open_cash_drawer === false) ? false : true;

			if (openCashdrawer) {
				commands.push(['7008', '1']);
			}

			let documentType = 'commercial_doc';

			if (isRefundVoid) {
				//We only need to check a single sale item, since the cause check has been done in the saleToReceiptCommands function
				const saleItemToCheck = sale.sale_items?.[0];

				switch (saleItemToCheck?.refund_cause_id) {
					case 6:
						documentType = "void_doc";
						break;
					default:
						documentType = "refund_doc";
				}
			}

			this.logEvent(`Print fiscal receipt on a ${this.printer.name} ${this.printer.ip_address} via tcp...`);

			// Print Fiscal Receipt
			try {
				await this.sendCommands(printerSocket, commands);
			} catch (error) {
				const lockStatus = await this.clearPendingDocuments(printerSocket);

				/*
				Document recovery fails if the printer
				- didn't have an open document (case 0)
				- had an open fiscal document whose transaction wasn't closed (case 1 through 4)
				- had an open non-fiscal document (case 8)
				*/
				if ([0, 1, 2, 3, 4, 8].includes(lockStatus)) {
					throw error;
				}
			}

			let documentData: Partial<SalesDocuments> | null = {};

			try {
				documentData = await this.lastReceiptInfo(printerSocket);
				documentData.document_type = documentType;
			} catch (error) {
				documentData = null;
			}

			successFunction(documentData ? [documentData] : []);
		} catch (error) {
			errorFunction(error);
		} finally {
			this.closeSocket(printerSocket);
		}
	}

	/**
	 * @desc print a courtesy receipt (also known as 'scontrino di cortesia', for italians)
	 *
	 * @param sale - the tilby sale you want to print
	 *
	 * @return successFunction with printed sale or errorFunction with errors
	 */
	public async printCourtesyReceipt(sale: Sales, options: any, successFunction: Function, errorFunction: Function) {
		// Build protocol commands
		let printerSocket: customPrinterSocket;
		
		try {
			if(!this.printer) {
				throw 'MISSING_PRINTER';
			}

			const commands = this.saleToCourtesyReceiptCommands(sale);
			printerSocket = await this.connectAndInitializePrinter(this.printer);

			this.logEvent(`Print courtesy receipt on a ${this.printer.name} ${this.printer.ip_address} via tcp...`);
			await this.sendCommands(printerSocket, commands);

			successFunction('OK');
		} catch (error) {
			errorFunction(error);
		} finally {
			this.closeSocket(printerSocket);
		}
	}

	/**
	 * @desc print a non fiscal receipt (also known as 'preconto', for italians)
	 *
	 * @param sale - the tilby sale you want to print
	 *
	 * @return successFunction with printed sale or errorFunction with errors
	 */
	public async printNonFiscal(sale: Sales, options: any, successFunction: Function, errorFunction: Function) {
		// Build protocol commands
		let printerSocket: customPrinterSocket;

		try {
			if(!this.printer) {
				throw 'MISSING_PRINTER';
			}

			const commands = this.saleToNonFiscalCommands(this.printer, sale, options);
			printerSocket = await this.connectAndInitializePrinter(this.printer);

			this.logEvent(`Print non fiscal receipt on a ${this.printer.name} ${this.printer.ip_address} via tcp...`);
			await this.sendCommands(printerSocket, commands);

			successFunction('OK');
		} catch (error) {
			errorFunction(error);
		} finally {
			this.closeSocket(printerSocket);
		}
	}

	/**
	 * @desc open cash drawer
	 *
	 * @return successFunction if drawer is correctly opened or errorFunction with errors
	 */
	public async openCashDrawer(successFunction: Function, errorFunction: Function) {
		let printerSocket: customPrinterSocket;

		try {
			if(!this.printer) {
				throw 'MISSING_PRINTER';
			}

			printerSocket = await this.isAvailable(this.printer);

			this.logEvent(`Opening cash drawer on a ${this.printer.name} ${this.printer.ip_address} via tcp...`);
			await this.sendCommands(printerSocket, [['7008', '1']]);

			successFunction('OK');
		} catch (error) {
			errorFunction(error);
		} finally {
			this.closeSocket(printerSocket);
		}
	}

	/**
	 * @desc do a daily closing
	 *
	 * @return successFunction  (with the closing receipt in ws mode, or 'OK' in bt mode) if close is correctly done or errorFunction with errors
	 */
	public async dailyClosing(successFunction: Function, errorFunction: Function) {
		//TOOD: save closing info and vat
		let printerSocket: customPrinterSocket;

		try {
			if(!this.printer) {
				throw 'MISSING_PRINTER';
			}

			printerSocket = await this.connectAndInitializePrinter(this.printer);

			this.logEvent(`Daily closing on a ${this.printer.name} ${this.printer.ip_address} via tcp...`);

			await this.sendCommands(printerSocket, [['2002']]);
			successFunction({});
		} catch (error) {
			errorFunction(error);
		} finally {
			this.closeSocket(printerSocket);
		}
	}

	public async readDgfeBetween(mode: ('read' | 'print'), from: string, to: string): Promise<string> {
		if (!['print'].includes(mode)) {
			throw 'METHOD_DOES_NOT_EXIST';
		}

		let printerSocket: customPrinterSocket;

		try {
			if(!this.printer) {
				throw 'MISSING_PRINTER';
			}

			printerSocket = await this.connectAndInitializePrinter(this.printer);

			this.logEvent(`${mode}ing DGFE on a ${this.printer.name} ${this.printer.ip_address} via tcp...`);

			await this.sendCommands(printerSocket, [['8001', from, to, '0']]);
		} finally {
			this.closeSocket(printerSocket);
		}

		return '';
	}

	/**
	 * @desc do a daily read
	 *
	 * @return promise
	 */
	public async dailyRead(): Promise<any> {
		if(!this.printer) {
			throw 'MISSING_PRINTER';
		}

		const commands: printerCommands = [['2003']];

		const printerSocket = await this.connectAndInitializePrinter(this.printer);
		this.logEvent(`Daily read on a ${this.printer.name} ${this.printer.ip_address} via tcp...`);

		try {
			await this.sendCommands(printerSocket, commands);
			return {};
		} finally {
			this.closeSocket(printerSocket);
		}
	}

	/**
	 * @description printFreeNonFiscal
	 */
	public async printFreeNonFiscal(lines: string[], options?: PrintFreeOptions): Promise<void> {
		let printerSocket: customPrinterSocket;
		
		try {
			if(!this.printer) {
				throw 'MISSING_PRINTER';
			}

			printerSocket = await this.connectAndInitializePrinter(this.printer);
			
			this.logEvent(`Print free non-fiscal on a ${this.printer.name} ${this.printer.ip_address} via tcp...`);

			const freeCommands = this.linesToCommands(lines, options);
			await this.sendCommands(printerSocket, freeCommands);
		} finally {
			this.closeSocket(printerSocket);
		}
	}

	/**
	 * @description print a free non fiscal receipt using a structured document layout
	 * @param printer, the target fiscal printer
	 * @param doc, the structured document layout (SimplifiedLayout)
	 * @param options, layout options
	 * @return Promise that resolves when print completes
	 */
	public async printFreeDocumentLayout(printer: Printers, doc: SimplifiedDocument, options?: any): Promise<void> {
		let printerSocket: customPrinterSocket;

		try {
			if (!printer) {
				throw 'MISSING_PRINTER';
			}

			printerSocket = await this.connectAndInitializePrinter(printer);

			this.logEvent(`Print free document layout on a ${printer.name} ${printer.ip_address} via tcp...`);

			const printerColumns = printer.columns || 42; // Default columns for Custom printers

			// Start a new non-fiscal document (CL: command 4001)
			const commands: printerCommands = [['4001', '4', '0', '000000000']];

			for (const line of doc) {
				switch (line.type) {
					case 'text': {
						let content = line.content || '';
						let fontAttr = '1'; // Default: normal font
						const style = line.style || {};

						// Mapping of Custom RT character attributes (4003)
						if (style.doubleHeight) {
							fontAttr = '4';
						} else if (style.doubleWidth) {
							fontAttr = '5';
						} else if (style.bold) {
							fontAttr = '2';
						}

						// Text alignment handling via padding
						if (style.align) {
							const align = style.align;

							if (align === 'center') {
								content = padCenter(content, printerColumns);
							} else if (align === 'right') {
								content = content.padStart(printerColumns, ' ');
							}
						}

						const cleanLine = cleanUpSpecialChars(content).slice(0, printerColumns);
						const lineLength = getLineLength(cleanLine);

						commands.push(['4003', fontAttr, lineLength, cleanLine]);
						break;
					}

					case 'barcode': {
						/* 
						const rawFormat = line.format || 'CODE39';
						const barcodeType = barcodeTypeCodes[rawFormat as keyof typeof barcodeTypeCodes] || '03';
						const hriPos = line.hri || '2';
						const barcodeVal = line.value || '';
						const cleanVal = cleanUpSpecialChars(barcodeVal);

						commands.push(['3023', barcodeType, hriPos, '4', getLineLength(cleanVal, 3), cleanVal]);
						*/
						break;
					}

					case 'qrcode': {
						const qrValue = line.value || '';
						const cleanVal = cleanUpSpecialChars(qrValue);

						commands.push(['3023', barcodeTypeCodes.QRCODE, '0', '4', getLineLength(cleanVal, 3), cleanVal]);
						break;
					}

					case 'logo': {
						// Custom RT command 4002 to print the logo stored in the printer's flash memory
						commands.push(['4002', '1']);
						break;
					}

					case 'pulse': {
						commands.push(['7008', '1']);
						break;
					}
				}
			}

			// Close and cut the non-fiscal document (command 4004)
			commands.push(['4004']);

			await this.sendCommands(printerSocket, commands);
		} finally {
			this.closeSocket(printerSocket);
		}
	}

	public async displayText(printer: Printers, textLines: string[]) {
		const dispLineLength = 20;
		let dataToSend = '';

		for (let i = 0; i < 2; i++) {
			dataToSend += (textLines[i] || '').slice(0, dispLineLength).padEnd(dispLineLength);
		}

		const displayCommands = [['7007', dataToSend]];

		let printerSocket: customPrinterSocket;

		try {
			printerSocket = await this.isAvailable(printer);

			this.logEvent(`Sending display message on a ${printer.name} ${printer.ip_address} via tcp...`);
			await this.sendCommands(printerSocket, displayCommands);
		} finally {
			this.closeSocket(printerSocket);
		}
	}

	/**
	 * isReachable
	 */
	public async isReachable(successFunction: Function, errorFunction: Function) {
		let printerSocket: customPrinterSocket;

		try {
			if(!this.printer) {
				throw 'MISSING_PRINTER';
			}

			printerSocket = await this.isAvailable(this.printer);
			successFunction();
		} catch (error) {
			errorFunction(error);
		} finally {
			this.closeSocket(printerSocket);
		}
	}
}

angular.module('printers').factory('CustomRTDriver', CustomRTDriver);